/**
 * Station Media Management Types
 * Types for the new station images feature with icon, banner, and gallery support
 */

export type StationImageType = 'icon' | 'banner' | 'gallery';

export type ImageModerationStatus = 'pending' | 'pending_review' | 'confirmed' | 'rejected';

export type UploadStatus = 'pending' | 'uploading' | 'confirming' | 'completed' | 'error';

/**
 * Image data returned from the backend API
 */
export interface IStationMediaImage {
  id: string;
  url: string;
  content_type: string;
  size: number;
  status: ImageModerationStatus;
  created_at: string;
  uploaded_at?: string;
  order?: number; // For gallery ordering
  review_reason?: string; // Why manual review is needed (when status is pending_review)
  approved_at?: string; // When the image was manually approved
  rejection_reason?: string | null; // Why the image was rejected
  rejected_by?: string | null; // Who rejected the image
  rejected_at?: string | null; // When the image was rejected
}

/**
 * Pending images awaiting review (for icon/banner replacement flow)
 */
export interface IPendingImages {
  icon: IStationMediaImage | null;
  banner: IStationMediaImage | null;
}

/**
 * Response from GET /{location_id}/images
 * Note: The `pending` object holds icon/banner images that are being uploaded
 * or awaiting review. Gallery images are always in the `gallery` array.
 */
export interface IStationImagesResponse {
  icon: IStationMediaImage | null;
  banner: IStationMediaImage | null;
  gallery: IStationMediaImage[];
  pending?: IPendingImages;
  rejected?: {
    icon?: IStationMediaImage | null;
    banner?: IStationMediaImage | null;
    gallery?: IStationMediaImage[];
  };
}

/**
 * Image currently being uploaded (client-side state)
 */
export interface IUploadingImage {
  id: string; // Temporary client-side ID (e.g., "temp-123456-0.789")
  file: File;
  type: StationImageType;
  progress: number; // 0-100
  status: UploadStatus;
  error?: string;
  previewUrl: string; // Object URL for instant preview
  serverImageId?: string; // Populated after upload-url response
}

/**
 * Response from POST /{location_id}/images/upload-url
 */
export interface IUploadUrlResponse {
  image_id: string;
  upload_url: string;
  content_type: string;
  blob_name: string;
  expiration_minutes: number;
}

/**
 * Response from POST /{location_id}/images/confirm
 */
export interface IConfirmUploadResponse {
  image_id: string;
  url: string;
  status: 'confirmed' | 'pending_review';
  image_type: StationImageType;
  message?: string; // Present when status is pending_review
}

/**
 * Error response for moderation failures
 */
export interface IModerationError {
  error: 'content_moderation_failed';
  message: string;
  details: {
    reason: string;
  };
}

/**
 * Combined state for station images (query + uploading)
 */
export interface IStationImagesState {
  icon: IStationMediaImage | null;
  banner: IStationMediaImage | null;
  gallery: IStationMediaImage[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Configuration for each image type
 */
export interface ImageTypeConfig {
  maxSize: number; // bytes
  maxCount: number;
  acceptTypes: string[];
  acceptExtensions: string;
  label: string;
  description: string;
  aspectRatio?: number; // Width / Height ratio (e.g., 1 for square, 3 for 3:1)
  outputWidth?: number; // Target output width in pixels
  outputHeight?: number; // Target output height in pixels
}

/**
 * Validation result for image files
 */
export interface ImageValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Configuration constants for image types
 */
export const IMAGE_TYPE_CONFIGS: Record<StationImageType, ImageTypeConfig> = {
  icon: {
    maxSize: 2 * 1024 * 1024, // 2 MB
    maxCount: 1,
    acceptTypes: ['image/png', 'image/jpeg'],
    acceptExtensions: '.png,.jpg,.jpeg,.webp',
    label: 'Station Icon',
    description: 'Square image. Max 2MB.',
    aspectRatio: 1, // 1:1 square
    outputWidth: 512,
    outputHeight: 512,
  },
  banner: {
    maxSize: 5 * 1024 * 1024, // 5 MB
    maxCount: 1,
    acceptTypes: ['image/png', 'image/jpeg'],
    acceptExtensions: '.png,.jpg,.jpeg,.webp',
    label: 'Station Banner',
    description: 'Header image for station details. Max 5MB.',
    aspectRatio: 3, // 3:1 wide
    outputWidth: 1200,
    outputHeight: 400,
  },
  gallery: {
    maxSize: 5 * 1024 * 1024, // 5 MB
    maxCount: 10,
    acceptTypes: ['image/png', 'image/jpeg'],
    acceptExtensions: '.png,.jpg,.jpeg,.webp',
    label: 'Gallery Images',
    description: 'Photo gallery for mobile app. Up to 10 images, max 5MB each.',
    // No aspectRatio - free cropping
  },
};

/**
 * Props for StationMediaManager component
 */
export interface StationMediaManagerProps {
  stationId: string;
  stationName?: string;
  disabled?: boolean;
  readOnly?: boolean;
  compact?: boolean; // For stationDetails view
}

/**
 * Props for individual section components
 */
export interface ImageSectionProps {
  stationId: string;
  currentImage: IStationMediaImage | null;
  pendingImage?: IStationMediaImage | null; // Image awaiting review (replacement flow)
  rejectedImage?: IStationMediaImage | null; // Most recently rejected image
  uploadingImage?: IUploadingImage;
  onUpload: (file: File) => Promise<void>;
  onDelete: (imageId: string) => Promise<void>;
  disabled?: boolean;
  readOnly?: boolean;
}

/**
 * Props for GallerySection with drag-to-reorder
 */
export interface GallerySectionProps {
  stationId: string;
  images: IStationMediaImage[];
  uploadingImages: IUploadingImage[];
  onUpload: (files: File[]) => Promise<void>;
  onDelete: (imageId: string) => Promise<void>;
  onReorder: (imageIds: string[]) => Promise<void>;
  maxCount: number;
  disabled?: boolean;
  readOnly?: boolean;
}

/**
 * Props for ImageCard component
 */
export interface ImageCardProps {
  image?: IStationMediaImage;
  uploadingImage?: IUploadingImage;
  aspectRatio?: number;
  showDragHandle?: boolean;
  onPreview?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}

/**
 * Props for ImageUploadDropzone
 */
export interface ImageUploadDropzoneProps {
  onDrop: (files: File[]) => void;
  accept: string[];
  maxSize: number;
  maxFiles?: number;
  disabled?: boolean;
  compact?: boolean;
  width?: number | string;
  height?: number | string;
  label?: string;
}

/**
 * Props for ImagePreviewModal
 */
export interface ImagePreviewModalProps {
  open: boolean;
  images: IStationMediaImage[];
  initialIndex?: number;
  onClose: () => void;
}

/**
 * State for managing crop modal in section components
 */
export interface CropModalState {
  open: boolean;
  file: File | null;
  previewUrl: string | null;
}

/**
 * Initial state for crop modal
 */
export const INITIAL_CROP_STATE: CropModalState = {
  open: false,
  file: null,
  previewUrl: null,
};
